import React, { Component } from "react";
import { connect } from "react-redux";
import {
  BrowserRouter,
  HashRouter,
  Route,
  Switch,
  Redirect,
} from "react-router-dom";

import { IntlProvider } from "react-intl";
import AppLocale from "./lang";
import ColorSwitcher from "./components/common/ColorSwitcher";
import NotificationContainer from "./components/common/react-notifications/NotificationContainer";
import { NotificationManager } from "./components/common/react-notifications";
import OfflineIndicator from "./components/common/OfflineIndicator";
import AutoUpdateModal from "./components/common/AutoUpdateModal";
import { isMultiColorActive, END_POINT } from "./constants/defaultValues";
import axios from "axios";
import "./helpers/OfflineAxiosAdapter";
import main from "./views";
import app from "./views/app";
import root from "./views/root";
import user from "./views/user";
import error from "./views/error";
import menuGuest from "./views/app/pages/product-guest";
import BookingOnline from "./views/guest/BookingOnline";
import tablePendingPayment from "./views/table-pending-payment";
import cashier from "./views/cashier";
import kitchen from "./views/kitchen";
import affiliate from "./views/affiliate"; // Import Affiliate App
import apiDocs from "./views/api-docs"; // Import API Docs Landing
import pricing from "./views/pricing"; // Import Pricing Page
import aiAdmin from "./views/ai-admin"; // Import AI Admin Dashboard
import tax from "./views/tax"; // Import Tax Calculator (thue.vmass.vn)
import fbAds from "./views/fb-ads"; // Import FB Ads Manager (ads.vmass.vn)
import { getDirection } from "./helpers/Utils";
import { getOrCreateWebPrintTerminalId } from "./helpers/WebPrintTerminal";
import { exchangeAuthHandoffFromUrl } from "./helpers/AuthHandoff";
import ElectronOnlinePrintAgent from "./components/ElectronOnlinePrintAgent";

// Detect Electron environment
const isElectron = !!window.electronAPI;
const Router = isElectron ? HashRouter : BrowserRouter;

// Capture launcher parameters before an unauthenticated route can redirect to login.
if (!isElectron) {
  getOrCreateWebPrintTerminalId();
}

const AuthRoute = ({ component: Component, authUser, ...rest }) => (
  <Route
    {...rest}
    render={(props) =>
      authUser ? (
        <Component {...props} />
      ) : (
        <Redirect
          to={{
            pathname: "/user/login",
            state: { from: props.location },
          }}
        />
      )
    }
  />
);

// Route bảo vệ /app - redirect cashier về /cashier
const CashierProtectedRoute = ({ component: Component, authUser, ...rest }) => {
  const ROLESETTINGS = localStorage.getItem("roleSettings")
    ? JSON.parse(localStorage.getItem("roleSettings"))
    : null;

  // Kiểm tra nếu user có role cashier
  const isCashier =
    ROLESETTINGS && ROLESETTINGS.find((role) => role.role_alias === "cashier");

  return (
    <Route
      {...rest}
      render={(props) => {
        if (!authUser) {
          return (
            <Redirect
              to={{
                pathname: "/user/login",
                state: { from: props.location },
              }}
            />
          );
        }
        // Nếu là cashier, redirect về /cashier
        if (isCashier) {
          return <Redirect to="/cashier" />;
        }
        return <Component {...props} />;
      }}
    />
  );
};

// Global axios interceptor: inject the JWT from localStorage as
// `Authorization: Bearer <token>` on every request to our backend.
// Skips requests that already set an Authorization header (e.g. OAuth flows
// or third-party APIs).
axios.interceptors.request.use((config) => {
  try {
    const url = config.url || "";
    // Skip attaching Authorization header for public endpoints
    if (
      url.includes("/check-domain-availability") ||
      url.includes("/auth/signup") ||
      url.includes("/auth/signin") ||
      url.includes("/otp/") ||
      url.includes("/booking/public")
    ) {
      return config;
    }

    const headers = config.headers || {};
    const hasAuth =
      headers.Authorization ||
      headers.authorization ||
      (headers.common &&
        (headers.common.Authorization || headers.common.authorization));
    if (!hasAuth) {
      const token = localStorage.getItem("access_token");
      if (token) {
        config.headers = {
          ...headers,
          Authorization: `Bearer ${token}`,
        };
      }
    }
  } catch (e) {
    // ignore — fall through without modifying the request
  }
  return config;
});

// Global axios response interceptor: handle 403 Forbidden permission denials
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error &&
      error.response &&
      error.response.status === 403 &&
      !error.config?.silent
    ) {
      const message =
        (error.response.data && error.response.data.responseText) ||
        "Bạn không có quyền thực hiện thao tác này. Vui lòng liên hệ quản lý.";
      NotificationManager.warning(message, "Không đủ quyền hạn", 4000);
    }
    return Promise.reject(error);
  }
);

class App extends Component {
  getResolvedAppLocale = (localeKey = this.props.locale) =>
    AppLocale[localeKey] || AppLocale.vi;

  syncDocumentLanguage = (localeKey = this.props.locale) => {
    const resolvedLocale = this.getResolvedAppLocale(localeKey);
    const localeTag = resolvedLocale.locale || "vi-VN";

    document.documentElement.lang = localeTag;

    const contentLanguageMeta = document.querySelector(
      'meta[http-equiv="Content-Language"]',
    );
    if (contentLanguageMeta) {
      contentLanguageMeta.setAttribute("content", localeTag);
    }
  };

  async componentDidMount() {
    this.syncDocumentLanguage();

    // 1. Direction Handling
    const direction = getDirection();
    if (direction.isRtl) {
      document.body.classList.add("rtl");
      document.body.classList.remove("ltr");
    } else {
      document.body.classList.add("ltr");
      document.body.classList.remove("rtl");
    }

    // 2. Security Check (Admin Session)
    if (localStorage.getItem("username") === "vmass") {
      const hasSession = document.cookie
        .split(";")
        .some((item) => item.trim().startsWith("admin_session="));

      const hasToken = localStorage.getItem("access_token");

      if (!hasSession && hasToken) {
        console.log("Admin session expired. Clearing storage...");
        localStorage.clear();
        window.location.href = "/user/login";
        return;
      }
    }

    // 3. Auth handoff from cross-subdomain redirects.
    try {
      if (await exchangeAuthHandoffFromUrl()) return;
    } catch (e) {
      console.error("Error exchanging auth handoff:", e);
    }

    // 4. Affiliate Tracking Logic
    const urlParamsSearch = new URLSearchParams(window.location.search);
    const refCode = urlParamsSearch.get("ref");
    if (refCode) {
      console.log("[Affiliate] Tracking ref code:", refCode);
      axios
        .get(`${END_POINT}/api/affiliate/track/${refCode}`, {
          withCredentials: true,
        })
        .then((res) => {
          if (res.status === 200) {
            console.log("[Affiliate] Tracking successful");
            // Tracking successful - cookie is set by server
          }
        })
        .catch((err) => console.error("Affiliate tracking error:", err));
    }

    // 5. Offline Sync Listeners for Electron
    if (isElectron && window.electronAPI?.offline?.onStatusChange) {
      // Register the listener and keep cleanup
      this._offlineStatusCleanup = window.electronAPI.offline.onStatusChange(
        (isOnline) => this.syncElectronOfflineData(isOnline),
      );

      // Perform initial check on mount
      window.electronAPI.offline.getStatus().then((status) => {
        this.syncElectronOfflineData(status);
      });
    }

    // 6. Sync printer settings from Electron on startup
    if (isElectron && window.electronAPI?.getPrinterSettings) {
      window.electronAPI.getPrinterSettings()
        .then((settings) => {
          if (settings) {
            if (settings.defaultPrinter) {
              localStorage.setItem("defaultPrinter", settings.defaultPrinter);
              localStorage.setItem("default_printer", settings.defaultPrinter);
            }
            if (settings.onlineCustomerPrinter) {
              localStorage.setItem("onlineCustomerPrinter", settings.onlineCustomerPrinter);
            }
            if (settings.onlineKitchenPrinter) {
              localStorage.setItem("onlineKitchenPrinter", settings.onlineKitchenPrinter);
            }
            if (settings.customerPaperWidthMm) {
              localStorage.setItem("customerPaperWidthMm", settings.customerPaperWidthMm);
            }
            if (settings.kitchenPaperWidthMm) {
              localStorage.setItem("kitchenPaperWidthMm", settings.kitchenPaperWidthMm);
            }
          }
        })
        .catch((err) => console.error("Error loading printer settings from Electron:", err));
    }
  }


  componentWillUnmount() {
    if (this._offlineStatusCleanup) {
      this._offlineStatusCleanup();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.locale !== this.props.locale) {
      this.syncDocumentLanguage();
    }
    if (prevProps.loginUser !== this.props.loginUser && this.props.loginUser) {
      this.syncElectronOfflineData();
    }
  }

  syncElectronOfflineData = async (knownOnline) => {
    if (!window.electronAPI?.isElectron || !window.electronAPI.sync?.fullSync) {
      return;
    }
    const isOnline = typeof knownOnline === "boolean"
      ? knownOnline
      : await window.electronAPI.offline.getStatus();
    if (!isOnline) return;

    const token = localStorage.getItem("access_token");
    const userId = localStorage.getItem("b") || localStorage.getItem("user_id");
    if (!token || !userId) return;
    if (this._electronSyncPromise) return this._electronSyncPromise;

    this._electronSyncPromise = window.electronAPI.sync.fullSync(userId, token)
      .then((result) => {
        if (!result.success) console.warn("[App] Offline sync incomplete:", result);
        return result;
      })
      .catch((error) => {
        console.error("[App] Full sync failed:", error);
        return { success: false, error: error.message };
      })
      .finally(() => {
        this._electronSyncPromise = null;
      });
    return this._electronSyncPromise;
  };

  render() {
    const { locale, loginUser } = this.props;
    const currentAppLocale = this.getResolvedAppLocale(locale);

    // SUBDOMAIN ROUTING LOGIC
    const hostname = window.location.hostname;
    // Check for 'affiliate' subdomain or specific development override
    const isAffiliate =
      hostname.startsWith("affiliate.") ||
      hostname.includes("localhost-affiliate") ||
      ["/forgot-password", "/reset-password"].some((path) =>
        window.location.pathname.startsWith(path),
      );
    // Check for 'api' subdomain (api.localhost for local, api.vmass.vn for production)
    const isApiDocs = hostname.startsWith("api.");
    // Check for 'ai' subdomain (ai.localhost for local, ai.vmass.vn for production)
    const isAiAdmin = hostname.startsWith("ai.");
    // Check for 'thue' subdomain (thue.localhost for local, thue.vmass.vn for production)
    const isTax = hostname.startsWith("thue.");
    // Check for 'marketing' subdomain (marketing.localhost for local, marketing.vmass.vn for production) 
    // Uses "marketing" instead of "ads" to bypass AdBlocker extensions blocking resources.
    const isFbAds = hostname.startsWith("marketing.");
    // const isAdmin = hostname.startsWith("admin."); // For future admin separation

    // AI Admin subdomain
    if (isAiAdmin) {
      return (
        <div className="h-100">
          <React.Fragment>
            <NotificationContainer />
            <Router>
              <Switch>
                <Route path="/" component={aiAdmin} />
              </Switch>
            </Router>
          </React.Fragment>
        </div>
      );
    }

    // Tax Calculator subdomain (thue.vmass.vn)
    if (isTax) {
      return (
        <div className="h-100">
          <React.Fragment>
            <NotificationContainer />
            <Router>
              <Switch>
                <Route path="/" component={tax} />
              </Switch>
            </Router>
          </React.Fragment>
        </div>
      );
    }

    // FB Ads Subdomain (ads.vmass.vn)
    if (isFbAds) {
      return (
        <div className="h-100">
          <IntlProvider
            locale={currentAppLocale.locale}
            messages={currentAppLocale.messages}
          >
            <React.Fragment>
              <NotificationContainer />
              <OfflineIndicator />
              <Router>
                <Switch>
                  <Route path="/" component={fbAds} />
                </Switch>
              </Router>
            </React.Fragment>
          </IntlProvider>
        </div>
      );
    }

    // API Docs subdomain
    if (isApiDocs) {
      return (
        <div className="h-100">
          <React.Fragment>
            <NotificationContainer />
            <OfflineIndicator />
            <AutoUpdateModal />
            <Router>
              <Switch>
                <Route path="/" component={apiDocs} />
              </Switch>
            </Router>
          </React.Fragment>
        </div>
      );
    }

    // Check for 'price' subdomain (price.localhost for local, price.vmass.vn for production)
    const isPricing = hostname.startsWith("price.");

    // Pricing subdomain
    if (isPricing) {
      return (
        <div className="h-100">
          <IntlProvider
            locale={currentAppLocale.locale}
            messages={currentAppLocale.messages}
          >
            <React.Fragment>
              <NotificationContainer />
              <OfflineIndicator />
              <AutoUpdateModal />
              <Router>
                <Switch>
                  <Route path="/" component={pricing} />
                </Switch>
              </Router>
            </React.Fragment>
          </IntlProvider>
        </div>
      );
    }

    if (isAffiliate) {
      return (
        <div className="h-100">
          <React.Fragment>
            <NotificationContainer />
            <OfflineIndicator />
            <AutoUpdateModal />
            <Router>
              <Switch>
                <Route path="/" component={affiliate} />
              </Switch>
            </Router>
          </React.Fragment>
        </div>
      );
    }

    return (
      <div className="h-100">
        <IntlProvider
          locale={currentAppLocale.locale}
          messages={currentAppLocale.messages}
        >
          <React.Fragment>
            <NotificationContainer />
            <OfflineIndicator />
            <AutoUpdateModal />
            {isMultiColorActive && <ColorSwitcher />}
            <Router>
              <React.Fragment>
                {isElectron && loginUser ? <ElectronOnlinePrintAgent /> : null}
                <Switch>
                  {/* Standard App Routes */}
                  <Route path="/menu" component={menuGuest} />
                  <Route
                    path="/booking-online/:store"
                    component={BookingOnline}
                  />
                  <AuthRoute
                    path="/cashier"
                    authUser={loginUser}
                    component={cashier}
                  />
                  <AuthRoute
                    path="/payment"
                    authUser={loginUser}
                    component={tablePendingPayment}
                  />
                  <AuthRoute
                    path="/kitchen"
                    authUser={loginUser}
                    component={kitchen}
                  />
                  <CashierProtectedRoute
                    path="/app"
                    authUser={loginUser}
                    component={app}
                  />
                  <AuthRoute path="/root" authUser={loginUser} component={root} />
                  <Route path="/user" component={user} />

                  {/* Fallback to affiliate if accessed via path on main domain, strictly for testing if subdomain fails, or redirection */}
                  {/* <Route path="/affiliate" component={affiliate} />  - Removed, strictly using subdomain now */}

                  <Route path="/error" exact component={error} />
                  <Route path="/" exact component={main} />
                  <Redirect to="/error" />
                </Switch>
              </React.Fragment>
            </Router>
          </React.Fragment>
        </IntlProvider>
      </div>
    );
  }
}

const mapStateToProps = ({ authUser, settings }) => {
  const { user: loginUser } = authUser;
  const { locale } = settings;
  return { loginUser, locale };
};
const mapActionsToProps = {};

export default connect(mapStateToProps, mapActionsToProps)(App);
